import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FaSearch,
  FaSpinner,
  FaTimes,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import {
  getCachedSearch,
  saveSearchCache,
} from '../utils/productSearchCache';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY = 300;
const MAX_PRODUCTS = 10;
const MAX_CATEGORIES = 3;
const MAX_BRANDS = 4;
const MAX_VARIANTS = 4;

const SearchBox = () => {
  const navigate = useNavigate();

  const searchWrapperRef = useRef(null);
  const inputRef = useRef(null);
  const resultRefs = useRef([]);

  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] =
    useState('');
  const [showDropdown, setShowDropdown] =
    useState(false);
  const [focusedIndex, setFocusedIndex] =
    useState(-1);

  // Stores products read from localStorage.
  const [cachedProductsData, setCachedProductsData] =
    useState(null);

  /*
   * Debounce search text.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
    }, SEARCH_DELAY);

    return () => clearTimeout(timer);
  }, [keyword]);

  const shouldSearch =
    debouncedKeyword.length >= MIN_SEARCH_LENGTH;

  /*
   * Load matching cached search results from localStorage.
   */
  useEffect(() => {
    if (!shouldSearch) {
      setCachedProductsData(null);
      return;
    }

    const cachedResult =
      getCachedSearch(debouncedKeyword);

    if (cachedResult?.data) {
      setCachedProductsData(cachedResult.data);
    } else {
      setCachedProductsData(null);
    }
  }, [debouncedKeyword, shouldSearch]);

  /*
   * Call the backend database.
   *
   * Cached localStorage data displays immediately.
   * The latest database response replaces it.
   */
  const {
    data: productsData,
    error,
    isFetching,
    refetch,
  } = useGetProductsQuery(
    {
      keyword: debouncedKeyword,
      pageNumber: 1,
      pageSize: MAX_PRODUCTS,
    },
    {
      skip: !shouldSearch,
      refetchOnFocus: true,
      refetchOnReconnect: true,

      // Check the database every 60 seconds while searching.
      pollingInterval: shouldSearch ? 60000 : 0,
    }
  );

  /*
   * Whenever fresh database data arrives:
   * 1. Update the displayed cached data.
   * 2. Update localStorage.
   */
  useEffect(() => {
    if (
      shouldSearch &&
      debouncedKeyword &&
      productsData
    ) {
      setCachedProductsData(productsData);

      saveSearchCache(
        debouncedKeyword,
        productsData
      );
    }
  }, [
    shouldSearch,
    debouncedKeyword,
    productsData,
  ]);

  /*
   * Prefer fresh database data.
   * Use localStorage while waiting for the database.
   */
  const products = useMemo(() => {
    if (!shouldSearch) {
      return [];
    }

    const sourceData =
      productsData || cachedProductsData;

    return (sourceData?.products || []).slice(
      0,
      MAX_PRODUCTS
    );
  }, [
    productsData,
    cachedProductsData,
    shouldSearch,
  ]);

  /*
   * Create unique category suggestions.
   */
  const categories = useMemo(() => {
    const categoryMap = new Map();

    products.forEach((product) => {
      const category = String(
        product?.category || ''
      ).trim();

      if (
        category &&
        !categoryMap.has(category.toLowerCase())
      ) {
        categoryMap.set(
          category.toLowerCase(),
          category
        );
      }
    });

    return Array.from(categoryMap.values()).slice(
      0,
      MAX_CATEGORIES
    );
  }, [products]);

  /*
   * Create unique brand suggestions.
   */
  const brands = useMemo(() => {
    const brandMap = new Map();

    products.forEach((product) => {
      (product?.details || []).forEach(
        (detail) => {
          const brand = String(
            detail?.brand || ''
          ).trim();

          if (
            brand &&
            !brandMap.has(brand.toLowerCase())
          ) {
            brandMap.set(
              brand.toLowerCase(),
              brand
            );
          }
        }
      );
    });

    return Array.from(brandMap.values()).slice(
      0,
      MAX_BRANDS
    );
  }, [products]);

  /*
   * Used for keyboard navigation.
   */
  const keyboardResults = useMemo(() => {
    return [
      ...categories.map((category) => ({
        type: 'category',
        value: category,
      })),

      ...brands.map((brand) => ({
        type: 'brand',
        value: brand,
      })),

      ...products.map((product) => ({
        type: 'product',
        value: product,
      })),
    ];
  }, [categories, brands, products]);

  /*
   * Reset keyboard selection when search text changes.
   */
  useEffect(() => {
    setFocusedIndex(-1);
    resultRefs.current = [];
  }, [keyword]);

  /*
   * Close dropdown when clicking outside.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(
          event.target
        )
      ) {
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, []);

  /*
   * Scroll keyboard-selected result into view.
   */
  useEffect(() => {
    if (
      focusedIndex >= 0 &&
      resultRefs.current[focusedIndex]
    ) {
      resultRefs.current[
        focusedIndex
      ].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [focusedIndex]);

  /*
   * Check database again when browser receives focus.
   */
  useEffect(() => {
    const handleWindowFocus = () => {
      if (shouldSearch) {
        refetch();
      }
    };

    window.addEventListener(
      'focus',
      handleWindowFocus
    );

    return () => {
      window.removeEventListener(
        'focus',
        handleWindowFocus
      );
    };
  }, [shouldSearch, refetch]);

  const closeDropdown = () => {
    setShowDropdown(false);
    setFocusedIndex(-1);
  };

  const clearSearch = () => {
    setKeyword('');
    setDebouncedKeyword('');
    setCachedProductsData(null);
    closeDropdown();

    inputRef.current?.focus();
  };

  const getProductName = (product) => {
    return (
      product?.englishname ||
      product?.productname ||
      product?.name ||
      'Product'
    );
  };

  /*
   * Telugu is displayed only when the field exists.
   * Search still works using the product name field.
   */
  const getTeluguName = (product) => {
    const englishName = getProductName(product);

    const teluguName = String(
      product?.teluguname || ''
    ).trim();

    if (
      !teluguName ||
      teluguName.toLowerCase() ===
        englishName.toLowerCase()
    ) {
      return '';
    }

    return teluguName;
  };

  const getProductImage = (product) => {
    if (product?.imageUrl) {
      return product.imageUrl;
    }

    if (product?.image) {
      return product.image;
    }

    for (const detail of product?.details || []) {
      const image =
        detail?.images?.[0]?.image ||
        detail?.images?.[0]?.url;

      if (image) {
        return image;
      }
    }

    return '/images/icon-192.png';
  };

  const getFirstAvailableBrand = (product) => {
    const detail = (product?.details || []).find(
      (item) => item?.brand
    );

    return detail?.brand || '';
  };

  /*
   * Collect unique financial variants.
   */
  const getProductVariants = (product) => {
    const variantMap = new Map();

    (product?.details || []).forEach(
      (detail) => {
        (detail?.financials || []).forEach(
          (financial) => {
            const quantity =
              financial?.quantity ?? '';

            const units =
              financial?.units ?? '';

            const price =
              financial?.dprice ??
              financial?.price ??
              0;

            const key = [
              quantity,
              units,
              price,
              detail?.brand || '',
            ].join('-');

            if (!variantMap.has(key)) {
              variantMap.set(key, {
                ...financial,
                brand: detail?.brand || '',
                detailId: detail?._id,
              });
            }
          }
        );
      }
    );

    return Array.from(
      variantMap.values()
    ).slice(0, MAX_VARIANTS);
  };

  const getDisplayPrice = (financial) => {
    const sellingPrice = Number(
      financial?.dprice ??
        financial?.price ??
        0
    );

    return Number.isFinite(sellingPrice)
      ? sellingPrice
      : 0;
  };

  const getOriginalPrice = (financial) => {
    const originalPrice = Number(
      financial?.price ?? 0
    );

    return Number.isFinite(originalPrice)
      ? originalPrice
      : 0;
  };

  const getVariantLabel = (financial) => {
    const quantity =
      financial?.quantity ?? '';

    const units =
      financial?.units ?? '';

    const label = `${quantity} ${units}`
      .replace(/\s+/g, ' ')
      .trim();

    return label || '1 QTY';
  };

  const isVariantInStock = (financial) => {
    return Number(
      financial?.countInStock || 0
    ) > 0;
  };

  const openProduct = (
    product,
    selectedVariant = null
  ) => {
    if (!product) {
      return;
    }

    const selectedBrand =
      selectedVariant?.brand ||
      getFirstAvailableBrand(product);

    const selectedQuantity =
      selectedVariant?.quantity !== undefined &&
      selectedVariant?.quantity !== null
        ? String(selectedVariant.quantity)
        : undefined;

    setKeyword('');
    setDebouncedKeyword('');
    setCachedProductsData(null);
    closeDropdown();

    if (product.slug) {
      navigate(`/product/${product.slug}`, {
        state: {
          brand: selectedBrand,
          quantity: selectedQuantity,
          qty: '1',
        },
      });

      return;
    }

    navigate(`/product/${product._id}`, {
      state: {
        brand: selectedBrand,
        quantity: selectedQuantity,
        qty: '1',
      },
    });
  };

  const openSearchResults = (
    searchValue = keyword
  ) => {
    const trimmedValue = String(
      searchValue || ''
    ).trim();

    if (!trimmedValue) {
      return;
    }

    setKeyword('');
    setDebouncedKeyword('');
    setCachedProductsData(null);
    closeDropdown();

    navigate(
      `/search?query=${encodeURIComponent(
        trimmedValue
      )}`
    );
  };

  const openCategory = (category) => {
    openSearchResults(category);
  };

  const openBrand = (brand) => {
    openSearchResults(brand);
  };

  const activateKeyboardResult = (result) => {
    if (!result) {
      return;
    }

    if (result.type === 'category') {
      openCategory(result.value);
      return;
    }

    if (result.type === 'brand') {
      openBrand(result.value);
      return;
    }

    if (result.type === 'product') {
      openProduct(result.value);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;

    setKeyword(value);
    setFocusedIndex(-1);

    if (
      value.trim().length >=
      MIN_SEARCH_LENGTH
    ) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleInputFocus = () => {
    if (
      keyword.trim().length >=
      MIN_SEARCH_LENGTH
    ) {
      setShowDropdown(true);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (
      focusedIndex >= 0 &&
      keyboardResults[focusedIndex]
    ) {
      activateKeyboardResult(
        keyboardResults[focusedIndex]
      );

      return;
    }

    openSearchResults();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeDropdown();
      return;
    }

    if (
      !showDropdown ||
      keyboardResults.length === 0
    ) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      setFocusedIndex((currentIndex) => {
        if (
          currentIndex >=
          keyboardResults.length - 1
        ) {
          return 0;
        }

        return currentIndex + 1;
      });

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      setFocusedIndex((currentIndex) => {
        if (currentIndex <= 0) {
          return keyboardResults.length - 1;
        }

        return currentIndex - 1;
      });

      return;
    }

    if (
      event.key === 'Enter' &&
      focusedIndex >= 0
    ) {
      event.preventDefault();

      activateKeyboardResult(
        keyboardResults[focusedIndex]
      );
    }
  };

  const highlightMatchingText = (text) => {
    const value = String(text || '');
    const searchValue = keyword.trim();

    if (!searchValue) {
      return value;
    }

    const escapedKeyword =
      searchValue.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );

    const regex = new RegExp(
      `(${escapedKeyword})`,
      'gi'
    );

    return value
      .split(regex)
      .map((part, index) => {
        const isMatch =
          part.toLowerCase() ===
          searchValue.toLowerCase();

        return isMatch ? (
          <mark
            key={`${part}-${index}`}
            className="rounded bg-yellow-100 px-0.5 text-inherit"
          >
            {part}
          </mark>
        ) : (
          <React.Fragment
            key={`${part}-${index}`}
          >
            {part}
          </React.Fragment>
        );
      });
  };

  const showSearchDropdown =
    showDropdown &&
    keyword.trim().length >=
      MIN_SEARCH_LENGTH;

  const noResults =
    shouldSearch &&
    !isFetching &&
    !error &&
    products.length === 0 &&
    categories.length === 0 &&
    brands.length === 0;

  let resultIndex = -1;

  return (
    <div
      ref={searchWrapperRef}
      className="relative w-full max-w-md md:max-w-lg lg:max-w-xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="flex h-10 items-center rounded-full border border-white/70 bg-white/75 px-3 shadow-[0_4px_16px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.95)] ring-1 ring-emerald-950/5 backdrop-blur-md focus-within:border-emerald-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-emerald-100 sm:h-11">
          <input
            ref={inputRef}
            type="text"
            value={keyword}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder="Search products, brands or categories..."
            autoComplete="off"
            aria-label="Search products"
            aria-expanded={showSearchDropdown}
            className="min-w-0 flex-grow bg-transparent py-2 pr-2 text-sm font-normal text-slate-800 outline-none placeholder:font-normal placeholder:text-slate-500 sm:text-base"
          />

          {keyword && (
            <button
              type="button"
              onClick={clearSearch}
              className="mr-1 flex h-7 w-7 flex-none items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Clear search"
            >
              <FaTimes className="h-3 w-3" />
            </button>
          )}

          <button
            type="submit"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-emerald-50/90 text-emerald-800 shadow-inner transition hover:bg-emerald-100"
            aria-label="Search"
          >
            {isFetching ? (
              <FaSpinner className="animate-spin" />
            ) : (
              <FaSearch />
            )}
          </button>
        </div>
      </form>

      {showSearchDropdown && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-[100] max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.22)]">
          {isFetching &&
            products.length === 0 && (
              <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-600">
                <FaSpinner className="animate-spin text-emerald-700" />

                <span>
                  Searching products...
                </span>
              </div>
            )}

          {isFetching &&
            products.length > 0 && (
              <div className="border-b border-emerald-100 bg-emerald-50 px-4 py-2 text-xs text-emerald-800">
                Showing saved results while
                checking the latest database
                data...
              </div>
            )}

          {error &&
            !isFetching &&
            products.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="font-semibold text-red-600">
                  Unable to search products
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  {error?.data?.message ||
                    error?.error ||
                    'Please try again.'}
                </p>
              </div>
            )}

          {noResults && (
            <div className="px-4 py-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                <FaSearch />
              </div>

              <p className="mt-3 font-semibold text-gray-900">
                No products found
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Try another product, brand,
                category or barcode.
              </p>
            </div>
          )}

          {!error &&
            categories.length > 0 && (
              <section>
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                    Categories
                  </p>
                </div>

                {categories.map((category) => {
                  resultIndex += 1;

                  const currentIndex =
                    resultIndex;

                  return (
                    <button
                      key={category}
                      ref={(element) => {
                        resultRefs.current[
                          currentIndex
                        ] = element;
                      }}
                      type="button"
                      onClick={() =>
                        openCategory(category)
                      }
                      onMouseEnter={() =>
                        setFocusedIndex(
                          currentIndex
                        )
                      }
                      className={`flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left transition ${
                        focusedIndex ===
                        currentIndex
                          ? 'bg-emerald-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="rounded-md bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                        Category
                      </span>

                      <span className="font-semibold text-gray-900">
                        {highlightMatchingText(
                          category
                        )}
                      </span>
                    </button>
                  );
                })}
              </section>
            )}

          {!error &&
            brands.length > 0 && (
              <section>
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                    Brands
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 border-b border-gray-100 px-4 py-3">
                  {brands.map((brand) => {
                    resultIndex += 1;

                    const currentIndex =
                      resultIndex;

                    return (
                      <button
                        key={brand}
                        ref={(element) => {
                          resultRefs.current[
                            currentIndex
                          ] = element;
                        }}
                        type="button"
                        onClick={() =>
                          openBrand(brand)
                        }
                        onMouseEnter={() =>
                          setFocusedIndex(
                            currentIndex
                          )
                        }
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          focusedIndex ===
                          currentIndex
                            ? 'border-amber-300 bg-amber-100 text-amber-900'
                            : 'border-amber-100 bg-amber-50 text-amber-800 hover:bg-amber-100'
                        }`}
                      >
                        {highlightMatchingText(
                          brand
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

          {!error &&
            products.length > 0 && (
              <section>
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                    Products
                  </p>
                </div>

                {products.map((product) => {
                  resultIndex += 1;

                  const currentIndex =
                    resultIndex;

                  const productName =
                    getProductName(product);

                  const teluguName =
                    getTeluguName(product);

                  const productImage =
                    getProductImage(product);

                  const productBrand =
                    getFirstAvailableBrand(
                      product
                    );

                  const variants =
                    getProductVariants(
                      product
                    );

                  return (
                    <div
                      key={product._id}
                      ref={(element) => {
                        resultRefs.current[
                          currentIndex
                        ] = element;
                      }}
                      onMouseEnter={() =>
                        setFocusedIndex(
                          currentIndex
                        )
                      }
                      className={`border-b border-gray-100 px-3 py-3 transition ${
                        focusedIndex ===
                        currentIndex
                          ? 'bg-emerald-50'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          openProduct(product)
                        }
                        className="flex w-full gap-3 text-left"
                      >
                        <div className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                          <img
                            src={productImage}
                            alt={productName}
                            className="h-full w-full object-contain p-1"
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.src =
                                '/images/icon-192.png';
                            }}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start gap-2">
                            <h3 className="min-w-0 text-sm font-bold leading-5 text-gray-900 sm:text-base">
                              {highlightMatchingText(
                                productName
                              )}

                              {teluguName && (
                                <span className="ml-1 font-normal text-gray-600">
                                  (
                                  {highlightMatchingText(
                                    teluguName
                                  )}
                                  )
                                </span>
                              )}
                            </h3>

                            {product.category && (
                              <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                                {highlightMatchingText(
                                  product.category
                                )}
                              </span>
                            )}
                          </div>

                          {productBrand && (
                            <p className="mt-1 text-xs text-gray-500">
                              Brand:{' '}
                              <span className="font-semibold text-gray-700">
                                {highlightMatchingText(
                                  productBrand
                                )}
                              </span>
                            </p>
                          )}
                        </div>
                      </button>

                      {variants.length > 0 && (
                        <div className="ml-[76px] mt-2 flex flex-wrap gap-2">
                          {variants.map(
                            (
                              financial,
                              variantIndex
                            ) => {
                              const sellingPrice =
                                getDisplayPrice(
                                  financial
                                );

                              const originalPrice =
                                getOriginalPrice(
                                  financial
                                );

                              const inStock =
                                isVariantInStock(
                                  financial
                                );

                              return (
                                <button
                                  key={
                                    financial._id ||
                                    `${product._id}-${variantIndex}`
                                  }
                                  type="button"
                                  disabled={!inStock}
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    if (inStock) {
                                      openProduct(
                                        product,
                                        financial
                                      );
                                    }
                                  }}
                                  className={`rounded-md border px-2.5 py-1.5 text-left text-xs transition ${
                                    inStock
                                      ? 'border-indigo-100 bg-indigo-50 text-indigo-900 hover:border-indigo-300 hover:bg-indigo-100'
                                      : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                                  }`}
                                >
                                  <span className="font-semibold">
                                    {getVariantLabel(
                                      financial
                                    )}
                                  </span>

                                  <span className="ml-2 font-bold">
                                    ₹{sellingPrice}
                                  </span>

                                  {originalPrice >
                                    sellingPrice &&
                                    sellingPrice >
                                      0 && (
                                      <span className="ml-1 text-[10px] text-gray-400 line-through">
                                        ₹
                                        {
                                          originalPrice
                                        }
                                      </span>
                                    )}

                                  {!inStock && (
                                    <span className="ml-2 text-[10px] font-semibold text-red-500">
                                      Out of stock
                                    </span>
                                  )}
                                </button>
                              );
                            }
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            )}

          {!error &&
            products.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  openSearchResults()
                }
                className="sticky bottom-0 w-full border-t border-gray-200 bg-white px-4 py-3 text-center text-sm font-bold text-emerald-700 shadow-[0_-4px_12px_rgba(15,23,42,0.05)] transition hover:bg-emerald-50"
              >
                View all results for “
                {keyword.trim()}”
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;